import { useMemo } from 'react'
import { AlertTriangle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { ChartFrame, RankedBars, FunnelBars } from '@/shared/ui/charts'
import { ChoroplethMap } from '@/shared/map/ChoroplethMap'
import { ErpTable, Td, Th } from '@/components/erp/primitives'
import type { Visual, VisualPoint } from '../api'
import { datasetRef, formatValue } from '../vocab'

/**
 * Draws a chart the model chose from data the platform supplied.
 *
 * ## The whole point of this component
 *
 * `visual.spec` is what the editor ASKED FOR — a chart kind, a dataset id, some
 * column names. `visual.resolved.points` is what the server FOUND in the brief
 * for that spec. This component only ever reads `resolved`. There is no path by
 * which a number the model wrote reaches the screen as a data point, which is
 * why a chart here cannot state a figure the platform does not hold.
 *
 * ## An unbound visual is shown, quietly, with its reason
 *
 * When the editor names a dataset that does not exist, or a column that is not in
 * it, the server keeps the row with `bindingStatus: 'UNBOUND_DROPPED'` and the
 * binder's explanation. Rendering that as a small grey note — rather than hiding
 * it — is what makes prompt drift visible to the people who can fix it, days
 * before anybody notices the prose getting worse.
 *
 * ## Hue carries nothing
 *
 * Following `shared/ui/charts`: magnitude is length and position, series are
 * small multiples, and the only place a colour scale appears is the choropleth,
 * where the data is ordered and the scale is sequential and single-hue.
 */

export function ReportVisual({
  visual,
  unitType,
  onSelectUnit,
  className,
}: {
  visual: Visual
  /** Which unit level a CHOROPLETH should draw. Defaults to ward. */
  unitType?: string
  onSelectUnit?: (unitId: string) => void
  className?: string
}) {
  const points = visual.resolved?.points ?? []
  const format = visual.resolved?.valueFormat
  const series = visual.resolved?.seriesNames ?? []

  const primary = useMemo(
    () => points.map((p) => ({
      label: p.label || '—',
      value: Number(p.values?.[0] ?? 0),
      hint: p.values && p.values.length > 1
        ? series.map((s, i) => `${s}: ${formatValue(p.values[i] ?? null, format)}`).join(' · ')
        : undefined,
    })),
    [points, series, format])

  if (visual.bindingStatus === 'UNBOUND_DROPPED') {
    return (
      <div className={cn('rounded-md border border-dashed border-border px-3 py-2.5', className)}>
        <div className="flex items-start gap-2">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />
          <div className="min-w-0">
            <div className="text-erp-xs font-medium">
              A chart was requested and could not be drawn
            </div>
            <div className="text-erp-xs text-muted-foreground mt-0.5">
              “{visual.title}” — {visual.bindingError}
            </div>
            <div className="text-erp-xs text-muted-foreground/70 mt-1">
              The editor asked for data the brief did not contain. Nothing was invented in its
              place; this note is the record.
            </div>
          </div>
        </div>
      </div>
    )
  }

  const caption = (
    <div className="mt-1.5 space-y-0.5">
      {visual.caption && (
        <div className="text-erp-xs text-muted-foreground">{visual.caption}</div>
      )}
      <div className="text-erp-xs text-muted-foreground/70 flex flex-wrap items-center gap-2">
        <code className="rounded bg-muted px-1 py-px">
          {datasetRef(visual.resolved?.datasetId)}
        </code>
        {visual.resolved?.sourceTitle && <span>{visual.resolved.sourceTitle}</span>}
        {visual.resolved?.truncated && (
          <span className="text-amber-700 dark:text-amber-400">
            source table was truncated — this is a sample
          </span>
        )}
      </div>
    </div>
  )

  const body = () => {
    switch (visual.kind) {
      case 'CHOROPLETH':
        return (
          <ChoroplethMap
            unitType={unitType ?? 'WARD'}
            values={points.map((p) => ({
              unitId: p.unitId ?? '',
              label: p.label,
              value: p.values?.[0] ?? null,
            })).filter((v) => v.unitId)}
            valueLabel={series[0] ?? visual.title}
            onSelect={onSelectUnit}
            height="h-[360px]"
          />
        )

      case 'KPI':
        return (
          <div className="flex flex-wrap gap-3">
            {(points.length > 0 ? points : [{ label: '—', values: [null] }]).slice(0, 4)
              .map((p, i) => (
                <div key={i} className="rounded-md border border-border px-3 py-2 min-w-[8rem]">
                  <div className="text-erp-xs text-muted-foreground truncate">{p.label}</div>
                  <div className="text-erp-lg font-semibold tabular-nums">
                    {formatValue(p.values?.[0] ?? null, format)}
                  </div>
                </div>
              ))}
          </div>
        )

      case 'GAUGE': {
        const value = points[0]?.values?.[0] ?? 0
        const max = Math.max(...points.map((p) => Number(p.values?.[1] ?? 100)), 1)
        const pct = Math.min(100, Math.max(0, (Number(value) / max) * 100))
        return (
          <div>
            <div className="flex items-baseline justify-between">
              <span className="text-erp-lg font-semibold tabular-nums">
                {formatValue(Number(value), format)}
              </span>
              <span className="text-erp-xs text-muted-foreground tabular-nums">
                of {formatValue(max, format)}
              </span>
            </div>
            <div className="mt-1.5 h-2.5 rounded-sm bg-muted/60 overflow-hidden">
              <div className="h-full rounded-r-sm" style={{
                width: `${pct}%`, backgroundColor: 'var(--chart-1)',
              }} />
            </div>
          </div>
        )
      }

      case 'FUNNEL':
        return (
          <FunnelBars
            stages={primary.map((p) => ({ label: p.label, value: p.value }))}
            formatValue={(n) => formatValue(n, format)}
          />
        )

      case 'LINE':
      case 'AREA':
        return <SeriesLine points={points} series={series} format={format} />

      case 'SCATTER':
        return <Scatter points={points} series={series} format={format} />

      case 'STACKED_BAR':
        return <SmallMultiples points={points} series={series} format={format} />

      case 'TABLE':
      case 'HEATMAP':
        return <PointsTable points={points} series={series} format={format} />

      case 'BAR':
      default:
        return (
          <RankedBars
            points={primary}
            formatValue={(n) => formatValue(n, format)}
            emptyMessage="The bound dataset had no numeric rows."
          />
        )
    }
  }

  return (
    <div className={cn('min-w-0', className)}>
      <ChartFrame title={visual.title} description={visual.subtitle ?? undefined}>
        {body()}
      </ChartFrame>
      {caption}
    </div>
  )
}

// ── Series shapes ───────────────────────────────────────────────────────────

/**
 * A line over an ordered x domain.
 *
 * Drawn as a `<polyline>` in a viewBox with `preserveAspectRatio="none"` for the
 * X axis only, which is safe here because a line has no mark whose thickness the
 * stretch could distort — unlike the bar charts in `shared/ui/charts`, which had
 * to be laid out with flex for exactly that reason.
 */
function SeriesLine({ points, series, format }: {
  points: VisualPoint[]
  series: string[]
  format?: string
}) {
  if (points.length < 2) {
    return <PointsTable points={points} series={series} format={format} />
  }
  const values = points.map((p) => Number(p.values?.[0] ?? 0))
  const min = Math.min(...values)
  const max = Math.max(...values)
  const span = max - min || 1
  const path = values.map((v, i) => {
    const x = (i / (values.length - 1)) * 100
    const y = 100 - ((v - min) / span) * 100
    return `${x.toFixed(2)},${y.toFixed(2)}`
  }).join(' ')

  return (
    <div>
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="h-32 w-full">
        <polyline
          points={path}
          fill="none"
          stroke="var(--chart-1)"
          strokeWidth={2}
          vectorEffect="non-scaling-stroke"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      </svg>
      <div className="flex justify-between text-erp-xs text-muted-foreground/70">
        <span>{points[0]?.label}</span>
        <span className="tabular-nums">
          {formatValue(min, format)} – {formatValue(max, format)}
        </span>
        <span>{points[points.length - 1]?.label}</span>
      </div>
    </div>
  )
}

/** Two measures against each other, one dot per unit. */
function Scatter({ points, series, format }: {
  points: VisualPoint[]
  series: string[]
  format?: string
}) {
  if (series.length < 2) {
    return (
      <RankedBars
        points={points.map((p) => ({ label: p.label, value: Number(p.values?.[0] ?? 0) }))}
        formatValue={(n) => formatValue(n, format)}
      />
    )
  }
  const dots = points.map((p) => ({
    label: p.label,
    x: Number(p.values?.[0] ?? 0),
    y: Number(p.values?.[1] ?? 0),
  }))
  const xMin = Math.min(...dots.map((d) => d.x))
  const xMax = Math.max(...dots.map((d) => d.x))
  const yMin = Math.min(...dots.map((d) => d.y))
  const yMax = Math.max(...dots.map((d) => d.y))
  const xSpan = xMax - xMin || 1
  const ySpan = yMax - yMin || 1

  return (
    <div>
      <svg viewBox="0 0 100 100" className="h-40 w-full">
        {dots.map((d, i) => (
          <circle
            key={i}
            cx={((d.x - xMin) / xSpan) * 96 + 2}
            cy={100 - (((d.y - yMin) / ySpan) * 92 + 4)}
            r={1.6}
            fill="var(--chart-1)"
            opacity={0.8}
          >
            <title>
              {d.label}: {series[0]} {formatValue(d.x, format)}, {series[1]}{' '}
              {formatValue(d.y, format)}
            </title>
          </circle>
        ))}
      </svg>
      <div className="flex justify-between text-erp-xs text-muted-foreground/70">
        <span>← {series[0]} →</span>
        <span>↑ {series[1]}</span>
      </div>
    </div>
  )
}

/**
 * Two related series as stacked panels, not a stacked bar.
 *
 * The design system's chart tokens fail a contrast check against each other, so
 * nothing in this app encodes a second series with a second hue. Small multiples
 * say the same thing and stay legible in greyscale and under any colour vision.
 */
function SmallMultiples({ points, series, format }: {
  points: VisualPoint[]
  series: string[]
  format?: string
}) {
  return (
    <div className="space-y-3">
      {series.slice(0, 3).map((name, index) => (
        <div key={name}>
          <div className="text-erp-xs text-muted-foreground mb-1">{name}</div>
          <RankedBars
            points={points.map((p) => ({
              label: p.label,
              value: Number(p.values?.[index] ?? 0),
            }))}
            formatValue={(n) => formatValue(n, format)}
          />
        </div>
      ))}
    </div>
  )
}

function PointsTable({ points, series, format }: {
  points: VisualPoint[]
  series: string[]
  format?: string
}) {
  if (points.length === 0) {
    return (
      <div className="py-6 text-center text-erp-xs text-muted-foreground">
        The bound dataset had no rows.
      </div>
    )
  }
  return (
    <div className="overflow-x-auto">
      <ErpTable head={
        <>
          <Th>Label</Th>
          {series.map((s) => <Th key={s} className="text-right">{s}</Th>)}
        </>
      }>
        {points.slice(0, 40).map((p, i) => (
          <tr key={i} className="border-t border-border">
            <Td>{p.label}</Td>
            {series.map((s, j) => (
              <Td key={s} className="text-right tabular-nums">
                {formatValue(p.values?.[j] ?? null, format)}
              </Td>
            ))}
          </tr>
        ))}
      </ErpTable>
    </div>
  )
}
